import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { FundingPolicyType } from '@shared/appModel';

export type LtcFinancialPlanningSliceState = LtcFinancialPlanningState

const initialState: LtcFinancialPlanningSliceState = {
  selectedAssets: [],
  priorityAssets: [],
  accountDetails: {},
  preferences: {
    deathBenefit: null,
    paymentStructure: null,
    benefitStructure: null,
  },
  selectedPolicyTypes: [],
  chosenPolicy: null,
  fundingAllocations: {},
  fundingModes: {},
  currentStep: 1,
};

const ltcFinancialPlanningSlice = createSlice({
  name: 'ltcFinancialPlanning',
  initialState,
  reducers: {
    setSelectedAssets(state, action: PayloadAction<LtcFinancialPlanningAssetType[]>) {
      state.selectedAssets = action.payload;
    },
    toggleAsset(state, action: PayloadAction<LtcFinancialPlanningAssetType>) {
      const asset = action.payload;
      const index = state.selectedAssets.indexOf(asset);
      if (index === -1) {
        state.selectedAssets.push(asset);
      } else {
        state.selectedAssets.splice(index, 1);
        // Also remove from priority assets if present
        const priorityIndex = state.priorityAssets.indexOf(asset);
        if (priorityIndex !== -1) {
          state.priorityAssets.splice(priorityIndex, 1);
        }
        // Remove account details for this asset
        delete state.accountDetails[asset];
        // Remove funding allocation for this asset
        delete state.fundingAllocations[asset];
        // Remove funding mode for this asset
        delete state.fundingModes[asset];
      }
    },
    setPriorityAssets(state, action: PayloadAction<LtcFinancialPlanningAssetType[]>) {
      state.priorityAssets = action.payload;
    },
    togglePriorityAsset(state, action: PayloadAction<LtcFinancialPlanningAssetType>) {
      const asset = action.payload;
      const index = state.priorityAssets.indexOf(asset);
      if (index === -1) {
        state.priorityAssets.push(asset);
      } else {
        state.priorityAssets.splice(index, 1);
      }
    },
    setAccountDetail(
      state,
      action: PayloadAction<{
        assetType: LtcFinancialPlanningAssetType;
        detail: LtcFinancialPlanningAccountDetail;
      }>,
    ) {
      state.accountDetails[action.payload.assetType] = action.payload.detail;
    },
    updateAccountDetail(
      state,
      action: PayloadAction<{
        assetType: LtcFinancialPlanningAssetType;
        field: keyof LtcFinancialPlanningAccountDetail;
        value: number | boolean;
      }>,
    ) {
      const { assetType, field, value } = action.payload;
      if (!state.accountDetails[assetType]) {
        state.accountDetails[assetType] = {
          currentValue: 0,
          futureContributions: 0,
          expectedReturn: 0,
          capitalGainsTaxRate: 0,
          isComplete: false,
        };
      }
      (state.accountDetails[assetType] as LtcFinancialPlanningAccountDetail)[field] = value as never;
    },
    setPreferences(state, action: PayloadAction<Partial<LtcFinancialPlanningPreferences>>) {
      state.preferences = { ...state.preferences, ...action.payload };
    },
    setDeathBenefitPreference(
      state,
      action: PayloadAction<LtcFinancialPlanningDeathBenefitPreference | null>,
    ) {
      state.preferences.deathBenefit = action.payload;
    },
    setPaymentStructurePreference(
      state,
      action: PayloadAction<LtcFinancialPlanningPaymentStructure | null>,
    ) {
      state.preferences.paymentStructure = action.payload;
    },
    setBenefitStructurePreference(
      state,
      action: PayloadAction<LtcFinancialPlanningBenefitStructure | null>,
    ) {
      state.preferences.benefitStructure = action.payload;
    },
    setSelectedPolicyTypes(state, action: PayloadAction<FundingPolicyType[]>) {
      state.selectedPolicyTypes = action.payload;
    },
    togglePolicyType(state, action: PayloadAction<FundingPolicyType>) {
      const policyType = action.payload;
      const index = state.selectedPolicyTypes.indexOf(policyType);
      if (index === -1) {
        state.selectedPolicyTypes.push(policyType);
      } else {
        state.selectedPolicyTypes.splice(index, 1);
      }
    },
    setChosenPolicy(state, action: PayloadAction<PolicyQuote | null>) {
      state.chosenPolicy = action.payload;
    },
    setFundingAllocation(
      state,
      action: PayloadAction<{ assetType: LtcFinancialPlanningAssetType; amount: number }>,
    ) {
      state.fundingAllocations[action.payload.assetType] = action.payload.amount;
    },
    toggleFundingSource(state, action: PayloadAction<LtcFinancialPlanningAssetType>) {
      const asset = action.payload;
      if (state.fundingAllocations[asset] !== undefined) {
        delete state.fundingAllocations[asset];
        delete state.fundingModes[asset];
      } else {
        // Initialize with 0, user will adjust via slider
        state.fundingAllocations[asset] = 0;
        // Default to lumpSum mode
        state.fundingModes[asset] = 'lumpSum';
      }
    },
    setFundingMode(
      state,
      action: PayloadAction<{
        assetType: LtcFinancialPlanningAssetType;
        mode: LtcFinancialPlanningFundingMode;
      }>,
    ) {
      const { assetType, mode } = action.payload;
      state.fundingModes[assetType] = mode;
      // Reset the allocation amount when switching modes to avoid confusion
      state.fundingAllocations[assetType] = 0;
    },
    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload;
    },
    resetLtcFinancialPlanning() {
      return initialState;
    },
  },
});

export const {
  setSelectedAssets,
  toggleAsset,
  setPriorityAssets,
  togglePriorityAsset,
  setAccountDetail,
  updateAccountDetail,
  setPreferences,
  setDeathBenefitPreference,
  setPaymentStructurePreference,
  setBenefitStructurePreference,
  setSelectedPolicyTypes,
  togglePolicyType,
  setChosenPolicy,
  setFundingAllocation,
  setFundingMode,
  toggleFundingSource,
  setCurrentStep,
  resetLtcFinancialPlanning,
} = ltcFinancialPlanningSlice.actions;

export const ltcFinancialPlanningReducer = ltcFinancialPlanningSlice.reducer;

